import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, TrendingDown, Download, RefreshCw } from 'lucide-react';
import { cashFlowService } from '../../services/cashFlowService';
import { format, startOfMonth, endOfMonth } from 'date-fns';

export default function CashFlowManagementDashboard() {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [bankBalances, setBankBalances] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingBalance, setEditingBalance] = useState({ Inter: false, 'Mercado Pago': false });
  const [tempBalances, setTempBalances] = useState({ Inter: 0, 'Mercado Pago': 0 });
  const [saving, setSaving] = useState(false);

  // Remove real-time subscription - use manual refresh instead
  // useCashFlowRealtime(loadDashboardData); // REMOVED FOR PERFORMANCE

  useEffect(() => {
    loadCashFlowData();
  }, [selectedDate]);

  const loadCashFlowData = async () => {
    try {
      setLoading(true);
      setError('');

      const date = new Date(selectedDate);
      const startDate = format(startOfMonth(date), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(date), 'yyyy-MM-dd');

      const [balances, receivablesData, expensesData] = await Promise.all([
        cashFlowService?.getBankBalances(selectedDate),
        cashFlowService?.getReceivables(startDate, endDate),
        cashFlowService?.getExpenses(startDate, endDate)
      ]);

      setBankBalances(balances || []);
      setReceivables(receivablesData || []);
      setExpenses(expensesData || []);

      // Initialize temp balances
      const balanceMap = {};
      (balances || [])?.forEach(b => {
        balanceMap[b.bankName] = b?.balance || 0;
      });
      setTempBalances(balanceMap);
    } catch (err) {
      console.error('Error loading cash flow data:', err);
      setError(err?.message || 'Erro ao carregar dados do fluxo de caixa');
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceEdit = (bankName) => {
    setEditingBalance({ ...editingBalance, [bankName]: true });
  };

  const handleBalanceSave = async (bankName) => {
    try {
      setSaving(true);
      setError('');
      
      const balanceValue = tempBalances?.[bankName] || 0;
      await cashFlowService?.upsertBankBalance(bankName, balanceValue, selectedDate);
      
      setEditingBalance({ ...editingBalance, [bankName]: false });
      await loadCashFlowData();
    } catch (err) {
      console.error('Error saving balance:', err);
      setError(`Erro ao salvar saldo: ${err?.message || 'Erro desconhecido'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBalanceChange = (bankName, value) => {
    const numValue = parseFloat(value) || 0;
    setTempBalances({ ...tempBalances, [bankName]: numValue });
  };

  const handleExport = async () => {
    try {
      const date = new Date(selectedDate);
      const month = date?.getMonth() + 1;
      const year = date?.getFullYear();

      const reportData = await cashFlowService?.exportMonthlyReport(month, year);
      
      // Create CSV content
      let csv = `Relatório de Fluxo de Caixa - ${month}/${year}\n\n`;
      
      csv += 'Saldos Bancários\n';
      csv += 'Banco,Saldo\n';
      reportData?.balances?.forEach(b => {
        csv += `${b?.bankName},R$ ${b?.balance?.toFixed(2)}\n`;
      });
      
      csv += '\nEntradas (Negociações)\n';
      csv += 'Data,Cliente,Título,Valor,Operador\n';
      reportData?.receivables?.forEach(r => {
        csv += `${r?.dueDate},${r?.clientName},${r?.negotiationTitle},R$ ${r?.amount?.toFixed(2)},${r?.operator}\n`;
      });
      
      csv += '\nSaídas (Despesas)\n';
      csv += 'Data,Descrição,Categoria,Valor\n';
      reportData?.expenses?.forEach(e => {
        csv += `${e?.expenseDate},${e?.description},${e?.category},R$ ${e?.amount?.toFixed(2)}\n`;
      });
      
      csv += '\nResumo\n';
      csv += `Saldo Inicial,R$ ${reportData?.summary?.initialTotal?.toFixed(2)}\n`;
      csv += `Total de Entradas,R$ ${reportData?.summary?.totalIncome?.toFixed(2)}\n`;
      csv += `Total de Saídas,R$ ${reportData?.summary?.totalExpenses?.toFixed(2)}\n`;
      csv += `Fluxo de Caixa Líquido,R$ ${reportData?.summary?.netCashFlow?.toFixed(2)}\n`;
      csv += `Saldo Final,R$ ${reportData?.summary?.finalBalance?.toFixed(2)}\n`;

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `fluxo_caixa_${month}_${year}.csv`;
      link?.click();
    } catch (err) {
      setError(err?.message);
    }
  };

  const calculateTotals = () => {
    const totalBalance = bankBalances?.reduce((sum, b) => sum + b?.balance, 0);
    const totalIncome = receivables?.reduce((sum, r) => sum + r?.amount, 0);
    const totalExpenses = expenses?.reduce((sum, e) => sum + e?.amount, 0);
    const currentBalance = totalBalance + totalIncome - totalExpenses;

    return { totalBalance, totalIncome, totalExpenses, currentBalance };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Fluxo de Caixa</h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={loadCashFlowData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <RefreshCw className="w-5 h-5" />
                Atualizar
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Download className="w-5 h-5" />
                Exportar
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-medium">Erro:</p>
                  <p className="text-sm mt-1">{error}</p>
                </div>
                <button
                  onClick={() => setError('')}
                  className="ml-4 text-red-500 hover:text-red-700"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bank Balances Section - 25% */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Saldos Bancários</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Inter Bank */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Inter</span>
                {!editingBalance?.Inter && !saving ? (
                  <button
                    onClick={() => handleBalanceEdit('Inter')}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Editar
                  </button>
                ) : null}
              </div>
              {editingBalance?.Inter ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    value={tempBalances?.Inter || 0}
                    onChange={(e) => handleBalanceChange('Inter', e?.target?.value)}
                    disabled={saving}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <button
                    onClick={() => handleBalanceSave('Inter')}
                    disabled={saving}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  R$ {((bankBalances?.find(b => b?.bankName === 'Inter')?.balance) || 0)?.toFixed(2)}
                </p>
              )}
            </div>

            {/* Mercado Pago */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Mercado Pago</span>
                {!editingBalance?.['Mercado Pago'] && !saving ? (
                  <button
                    onClick={() => handleBalanceEdit('Mercado Pago')}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Editar
                  </button>
                ) : null}
              </div>
              {editingBalance?.['Mercado Pago'] ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    value={tempBalances?.['Mercado Pago'] || 0}
                    onChange={(e) => handleBalanceChange('Mercado Pago', e?.target?.value)}
                    disabled={saving}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <button
                    onClick={() => handleBalanceSave('Mercado Pago')}
                    disabled={saving}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              ) : (
                <p className="text-2xl font-bold text-gray-900">
                  R$ {((bankBalances?.find(b => b?.bankName === 'Mercado Pago')?.balance) || 0)?.toFixed(2)}
                </p>
              )}
            </div>

            {/* Total */}
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
              <span className="text-sm font-medium text-blue-600">Total</span>
              <p className="text-2xl font-bold text-blue-700">
                R$ {totals?.totalBalance?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Transaction Lists - 70% */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Receivables (Income) */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Entradas (Negociações)</h2>
              <div className="flex items-center gap-2 text-green-600">
                <TrendingUp className="w-5 h-5" />
                <span className="font-bold">R$ {totals?.totalIncome?.toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {receivables?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nenhuma entrada no período</p>
              ) : (
                receivables?.map((item) => (
                  <div key={item?.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{item?.negotiationTitle}</span>
                      <span className="text-green-600 font-bold">+R$ {item?.amount?.toFixed(2)}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Cliente: {item?.clientName}</p>
                      <p>Data: {format(new Date(item.dueDate), 'dd/MM/yyyy')}</p>
                      <p>Operador: {item?.operator}</p>
                      {item?.paymentMethod && <p>Método: {item?.paymentMethod}</p>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Expenses (Outflow) */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Saídas (Despesas)</h2>
              <div className="flex items-center gap-2 text-red-600">
                <TrendingDown className="w-5 h-5" />
                <span className="font-bold">R$ {totals?.totalExpenses?.toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {expenses?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nenhuma despesa no período</p>
              ) : (
                expenses?.map((item) => (
                  <div key={item?.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">{item?.description}</span>
                      <span className="text-red-600 font-bold">-R$ {item?.amount?.toFixed(2)}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>Categoria: {item?.category}</p>
                      <p>Data: {format(new Date(item.expenseDate), 'dd/MM/yyyy')}</p>
                      {item?.paymentMethod && <p>Método: {item?.paymentMethod}</p>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Summary Section - 5% */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Resumo</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <span className="text-sm text-gray-600">Saldo Inicial</span>
              <p className="text-xl font-bold text-gray-900">R$ {totals?.totalBalance?.toFixed(2)}</p>
            </div>
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <span className="text-sm text-green-600">Total Entradas</span>
              <p className="text-xl font-bold text-green-700">+R$ {totals?.totalIncome?.toFixed(2)}</p>
            </div>
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <span className="text-sm text-red-600">Total Saídas</span>
              <p className="text-xl font-bold text-red-700">-R$ {totals?.totalExpenses?.toFixed(2)}</p>
            </div>
            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <span className="text-sm text-blue-600">Saldo Atual</span>
              <p className="text-xl font-bold text-blue-700">R$ {totals?.currentBalance?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}